<template>
  <div class="camera-scanner" :class="{ 'is-inset': inset }">
    <div v-if="error" class="camera-error">
      <p>
        <i class="far fa-exclamation-triangle"></i>
        {{ error }}
      </p>
      <b-button-group>
        <b-button variant="outline-primary" @click="start()">Réessayer</b-button>
        <b-button variant="outline-secondary" @click="close()">Fermer</b-button>
      </b-button-group>
      <!-- Le nom technique ne s'adresse pas au préparateur, mais c'est lui qui permet de
           trancher au support entre un refus, un navigateur trop ancien et un certificat
           local invalide. Discret, mais présent. -->
      <p v-if="errorName" class="camera-error-detail">{{ errorName }}</p>
    </div>

    <template v-else>
      <div class="camera-viewport" :class="{ 'is-hit': hit }">
        <video ref="video" playsinline muted autoplay></video>
        <div class="camera-reticle"></div>
        <div v-if="starting" class="camera-starting">Ouverture de l'appareil photo...</div>
      </div>

      <!-- Le bandeau dit ce que le lecteur a lu, et rien de plus : le viseur ignore si
           l'application a accepté ce code. Ce qu'elle en a fait se lit au-dessus, dans la
           liste restée visible, et dans le message que l'écran affiche. Annoncer un succès
           ici ferait passer un refus métier pour un ajout réussi. -->
      <p class="camera-status" :class="{ 'is-hit': hit }">
        <template v-if="lastValue">
          <i class="far fa-barcode-read"></i>
          Code lu : {{ lastValue }}
          <span class="camera-count">{{ scanCount }} lecture{{ scanCount > 1 ? "s" : "" }}</span>
        </template>
        <template v-else>Présentez le code barre ou le QR code dans le cadre</template>
      </p>

      <b-button variant="outline-secondary" class="camera-close btn" @click="close()">
        Fermer l'appareil photo
      </b-button>
    </template>
  </div>
</template>

<script>
/**
 * QR-006 : lecture d'un code par l'appareil photo, pour les appareils sans douchette.
 *
 * Le composant ne connaît rien du métier : il lit un code et émet `decoded` avec la valeur
 * brute, exactement comme si elle avait été tapée dans le champ de scan. La résolution
 * (code barre, code produit, URL de QR code) reste au mixin scannedValue.
 *
 * Le viseur prend la place du champ de saisie, dans la barre de scan, et n'occupe qu'un
 * tiers de la hauteur : la liste des références reste visible au-dessus, et c'est elle qui
 * confirme au préparateur que le scan a produit son effet. Une modale, elle, masquait
 * précisément ce qui sert de preuve. Le composant est monté et démonté avec le mode photo
 * (`v-if`), ce qui suffit à garantir que la caméra s'éteint quand on en sort.
 *
 * Le décodage est confié à ZXing (@zxing/library), portage JavaScript de la bibliothèque de
 * référence. C'est la seule des bibliothèques courantes qui lise à la fois les codes barres
 * à une dimension et les QR codes — le parc porte les deux : les anciennes étiquettes sont
 * en Code 128 (cf. le composant `barcode` de ReferenceDetail), les nouvelles en QR code
 * (QR-005). Lecture purement logicielle, donc identique sur tous les navigateurs.
 *
 * Le scan est continu : le viseur reste ouvert après une lecture, pour enchaîner plusieurs
 * références. Un même code n'est émis qu'une fois par DUPLICATE_GUARD_MS, sans quoi une
 * étiquette restée dans le cadre serait lue à chaque image.
 */
import {
  BarcodeFormat,
  BinaryBitmap,
  DecodeHintType,
  HTMLCanvasElementLuminanceSource,
  HybridBinarizer,
  MultiFormatReader
} from "@zxing/library";

/**
 * Formats recherchés. La liste est volontairement courte : ZXing essaie chaque décodeur
 * demandé sur chaque image, et chercher des symbologies que le parc n'utilise pas ne
 * coûterait que du temps de calcul sur un téléphone.
 *
 *   - QR_CODE : les étiquettes posées depuis QR-005 ;
 *   - CODE_128 : les étiquettes historiques (format par défaut de JsBarcode) ;
 *   - CODE_39, ITF : symbologies 1D voisines, pour une étiquette d'un autre fournisseur ;
 *   - EAN_13, EAN_8 : codes du commerce, sur les produits à la vente ;
 *   - DATA_MATRIX : marquage de petits matériels.
 */
const SCAN_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.DATA_MATRIX
];

/**
 * Cadence d'analyse. La caméra fournit 30 images par seconde, les analyser toutes ne rend
 * pas la lecture plus sûre et chauffe l'appareil ; 100 ms reste sous le seuil de perception.
 */
const FRAME_INTERVAL_MS = 100;

/**
 * Largeur d'analyse. La caméra filme en 1280 px, mais décoder à cette taille coûte plusieurs
 * fois plus cher sans mieux lire : le code présenté occupe déjà le cadre. 640 px garde assez
 * de définition pour les barres fines d'un Code 128 tenu à bout de bras.
 */
const MAX_SCAN_WIDTH = 640;

/** Un code resté dans le viseur ne doit pas être renvoyé à chaque image. */
const DUPLICATE_GUARD_MS = 2000;

/**
 * Délai au-delà duquel l'ouverture de la caméra est considérée comme bloquée.
 *
 * getUserMedia ne promet ni de réussir ni d'échouer : tant que l'utilisateur n'a pas répondu
 * à la demande d'autorisation du navigateur, la promesse reste simplement en attente —
 * indéfiniment s'il ne voit pas la bulle. Sans ce garde-fou, le viseur reste noir sans rien
 * expliquer, et chaque abandon compte pour le navigateur comme une demande ignorée : au
 * bout de trois, il met le site sous embargo et cesse de proposer l'autorisation.
 */
const START_TIMEOUT_MS = 10000;

/** Durée du retour visuel vert confirmant la lecture. */
const HIT_FEEDBACK_MS = 1200;

/** Bip de confirmation : fréquence, durée et volume d'une douchette de magasin. */
const BEEP_FREQUENCY_HZ = 1760;
const BEEP_DURATION_S = 0.08;
const BEEP_GAIN = 0.15;

export default {
  name: "CameraScanner",
  props: {
    /**
     * Reprend le retrait horizontal des barres de scan, où le champ remplacé porte lui-même
     * `margin: 20px 25px`. À désactiver quand le viseur est posé dans un formulaire, qui gère
     * déjà ses propres marges.
     */
    inset: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      /** Message d'erreur affiché à la place du viseur, null tant que tout va bien. */
      error: null,
      /** Nom technique de l'erreur (DOMException.name), pour le diagnostic. */
      errorName: null,
      /** Vrai entre la demande d'accès et la première image. */
      starting: false,
      /** Minuteur du garde-fou d'ouverture (cf. START_TIMEOUT_MS). */
      startTimer: null,
      /** Retour visuel d'une lecture réussie. */
      hit: false,
      hitTimer: null,
      /** Flux caméra en cours, conservé pour pouvoir le couper. */
      stream: null,
      /** Identifiant de la boucle d'analyse. */
      rafId: null,
      lastFrameAt: 0,
      /** Dernière valeur émise, affichée en confirmation et garde-fou anti-répétition. */
      lastValue: null,
      lastValueAt: 0,
      /** Nombre de lectures depuis l'ouverture du viseur. */
      scanCount: 0,
      /** Canvas d'analyse, hors du DOM : il ne sert qu'à donner ses pixels au décodeur. */
      canvas: null,
      /** Vrai dès la sortie du mode photo : une réponse tardive n'a plus à être exploitée. */
      closed: false
    };
  },
  created() {
    // Le lecteur n'a pas d'état entre deux images : une seule instance sert toute la vie du
    // composant, et sa construction (tables de décodage) n'est pas refaite à chaque image.
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS);

    this.reader = new MultiFormatReader();
    this.reader.setHints(hints);
  },
  mounted() {
    // Le composant n'existe que pendant le mode photo : son montage vaut demande d'ouverture.
    // On est encore dans la foulée du geste qui a choisi le mode, ce dont le son a besoin.
    this.prepareBeep();
    this.start();
  },
  beforeDestroy() {
    // Sortir du mode photo, ou quitter l'écran, doit éteindre la caméra : la laisser allumée
    // se voit (témoin lumineux) et consomme la batterie.
    this.closed = true;
    this.stop();
    this.releaseBeep();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    start() {
      this.error = null;
      this.errorName = null;
      this.starting = true;
      this.lastValue = null;
      this.armStartTimeout();

      // getUserMedia n'existe qu'en contexte sécurisé. Sur un serveur servi en clair, la
      // caméra n'est pas « refusée » : la fonction est absente, ce qui mérite un autre message.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.failToStart(
          window.isSecureContext === false
            ? "L'appareil photo n'est accessible qu'en HTTPS. Ouvrez l'application en https:// pour l'utiliser."
            : "Ce navigateur ne donne pas accès à l'appareil photo.",
          "mediaDevices indisponible"
        );
        return;
      }

      navigator.mediaDevices
        .getUserMedia({
          // `ideal` et non `exact` : sur un poste sans caméra arrière, mieux vaut la caméra
          // frontale qu'un refus sec.
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        })
        .then((stream) => {
          // Le mode photo a pu être quitté pendant que l'utilisateur répondait à la demande
          // d'autorisation : le flux arrive alors dans le vide, il faut le couper.
          if (this.closed) {
            stream.getTracks().forEach((track) => track.stop());
            return null;
          }

          this.stream = stream;
          return this.attachStream(stream);
        })
        .then(() => {
          if (!this.stream) {
            return;
          }
          this.clearStartTimeout();
          this.starting = false;
          this.loop();
        })
        .catch((error) => {
          this.failToStart(
            this.describeError(error),
            error && error.name ? error.name : null
          );
        });
    },
    /**
     * Branche le flux sur la balise <video>.
     *
     * Le rendu du viseur et la réponse de getUserMedia sont deux horloges indépendantes : on
     * laisse Vue finir son cycle avant d'aller chercher la référence, plutôt que de supposer
     * qu'elle est déjà là.
     */
    attachStream(stream) {
      return this.$nextTick().then(() => {
        const video = this.$refs.video;

        if (!video) {
          // Rien ne peut plus arriver à l'écran : mieux vaut le dire que laisser le viseur
          // noir sur son message d'ouverture.
          const error = new Error("viewport-missing");
          error.name = "ViewportMissing";
          throw error;
        }

        video.srcObject = stream;
        // Safari ne lance pas toujours la lecture seul, malgré l'attribut autoplay.
        return video.play().catch(() => null);
      });
    },
    stop() {
      this.clearStartTimeout();

      if (this.rafId !== null) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.hitTimer) {
        clearTimeout(this.hitTimer);
        this.hitTimer = null;
      }
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
      }
      this.starting = false;
      this.hit = false;
    },
    /**
     * Arme le garde-fou d'ouverture : si aucune image n'arrive dans le délai imparti, c'est
     * presque toujours qu'une demande d'autorisation attend une réponse hors de la page.
     */
    armStartTimeout() {
      this.clearStartTimeout();
      this.startTimer = setTimeout(() => {
        this.startTimer = null;

        if (!this.starting) {
          return;
        }

        this.failToStart(
          "L'appareil photo ne répond pas. Si le navigateur demande l'autorisation d'y accéder, acceptez-la : la demande s'affiche près de la barre d'adresse.",
          "autorisation en attente"
        );
      }, START_TIMEOUT_MS);
    },
    clearStartTimeout() {
      if (this.startTimer) {
        clearTimeout(this.startTimer);
        this.startTimer = null;
      }
    },
    /** Abandonne l'ouverture en affichant la raison : jamais d'échec muet. */
    failToStart(message, name) {
      this.clearStartTimeout();
      this.starting = false;
      this.error = message;
      this.errorName = name;

      // Un flux a pu être obtenu avant l'échec (viseur absent, par exemple) : le laisser
      // ouvert allumerait la caméra sans que rien ne s'affiche.
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    /**
     * Boucle d'analyse, cadencée par le rafraîchissement d'écran puis bridée à 10 Hz.
     *
     * Le décodage est synchrone : une image est analysée de bout en bout avant que la
     * suivante soit demandée, il n'y a donc pas d'analyses à empiler.
     */
    loop() {
      this.rafId = window.requestAnimationFrame(this.loop);

      if (!this.isFrameReady()) {
        return;
      }

      const now = Date.now();
      if (now - this.lastFrameAt < FRAME_INTERVAL_MS) {
        return;
      }
      this.lastFrameAt = now;

      const value = this.detectInFrame();
      if (value) {
        this.emitDecoded(value);
      }
    },
    isFrameReady() {
      const video = this.$refs.video;
      // HAVE_CURRENT_DATA : avant cela, videoWidth vaut 0 et l'analyse ne porte sur rien.
      return Boolean(video && video.readyState >= 2 && video.videoWidth);
    },
    /**
     * Analyse l'image courante.
     *
     * ZXing signale l'absence de code par une exception (NotFoundException), au même titre
     * qu'un code trouvé mais illisible (ChecksumException, FormatException) : sur un scan
     * continu, ces trois cas sont la situation normale — l'utilisateur n'a pas encore
     * présenté l'étiquette, ou le cadrage n'est pas bon. Aucun n'est remonté.
     *
     * @returns {?string} la valeur lue, ou null si l'image ne contient pas de code exploitable
     */
    detectInFrame() {
      const canvas = this.grabFrame();
      if (!canvas) {
        return null;
      }

      try {
        const source = new HTMLCanvasElementLuminanceSource(canvas);
        const bitmap = new BinaryBitmap(new HybridBinarizer(source));

        const result = this.reader.decode(bitmap);
        return result ? result.getText() : null;
      } catch (error) {
        return null;
      } finally {
        // Certains décodeurs 1D gardent une trace de l'image précédente : sans remise à
        // zéro, une lecture douteuse peut se propager sur les images suivantes.
        this.reader.reset();
      }
    },
    /** Recopie l'image courante dans le canvas d'analyse, réduite à MAX_SCAN_WIDTH. */
    grabFrame() {
      const video = this.$refs.video;

      if (!this.canvas) {
        this.canvas = document.createElement("canvas");
      }

      const scale = Math.min(1, MAX_SCAN_WIDTH / video.videoWidth);
      const width = Math.round(video.videoWidth * scale);
      const height = Math.round(video.videoHeight * scale);

      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }

      // `willReadFrequently` : le contexte est relu à chaque image, l'indication évite
      // l'aller-retour avec le GPU. Ignorée par les navigateurs qui ne la connaissent pas.
      const context = this.canvas.getContext("2d", { willReadFrequently: true });
      context.drawImage(video, 0, 0, width, height);

      return this.canvas;
    },
    /**
     * Émet la valeur lue, sauf si c'est la même qu'il y a moins de deux secondes : sans ce
     * garde-fou, une étiquette laissée dans le cadre serait scannée dix fois par seconde.
     */
    emitDecoded(value) {
      const now = Date.now();

      if (value === this.lastValue && now - this.lastValueAt < DUPLICATE_GUARD_MS) {
        return;
      }

      this.lastValue = value;
      this.lastValueAt = now;
      this.scanCount += 1;

      // La lecture est instantanée : sans retour, l'utilisateur ne sait pas si le code est
      // passé et continue de présenter l'étiquette.
      this.flashHit();

      this.$emit("decoded", value);
    },
    flashHit() {
      this.hit = true;
      if (this.hitTimer) {
        clearTimeout(this.hitTimer);
      }
      this.hitTimer = setTimeout(() => {
        this.hit = false;
        this.hitTimer = null;
      }, HIT_FEEDBACK_MS);

      this.playBeep();

      if (navigator.vibrate) {
        navigator.vibrate(60);
      }
    },
    /**
     * Prépare le contexte audio pendant le geste qui ouvre le viseur.
     *
     * Les navigateurs n'autorisent le son qu'à la suite d'une action de l'utilisateur : un
     * contexte créé plus tard, au moment de la première lecture, resterait muet.
     */
    prepareBeep() {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        return;
      }

      try {
        this.audioContext = new AudioContextClass();
        if (this.audioContext.state === "suspended") {
          this.audioContext.resume();
        }
      } catch (error) {
        // Le bip est un confort : son absence ne doit jamais empêcher de scanner.
        this.audioContext = null;
      }
    },
    /**
     * Bip court de confirmation, synthétisé plutôt que joué depuis un fichier : c'est le
     * signal qu'un préparateur connaît de sa douchette, et il fonctionne là où la vibration
     * n'existe pas (iOS) ou ne se sent pas (appareil posé sur un établi).
     */
    playBeep() {
      // Le contexte a pu ne pas être créé au montage (navigateur qui l'interdit hors geste),
      // ou avoir été fermé : on retente ici, où l'on est certain qu'un geste a eu lieu.
      if (!this.audioContext || this.audioContext.state === "closed") {
        this.prepareBeep();
      }

      if (!this.audioContext) {
        return;
      }

      // resume() est asynchrone : jouer sans l'attendre planifie le son sur une horloge
      // encore à l'arrêt, et rien ne sort. C'est la cause la plus courante d'un bip muet.
      if (this.audioContext.state === "suspended") {
        this.audioContext
          .resume()
          .then(() => this.emitBeep())
          .catch(() => null);
        return;
      }

      this.emitBeep();
    },
    emitBeep() {
      try {
        const context = this.audioContext;
        if (!context || context.state !== "running") {
          return;
        }

        const oscillator = context.createOscillator();
        const gain = context.createGain();

        oscillator.type = "square";
        oscillator.frequency.value = BEEP_FREQUENCY_HZ;

        // Attaque et extinction en rampe : un gain posé sèchement claque dans le haut-parleur.
        const startedAt = context.currentTime;
        gain.gain.setValueAtTime(0.0001, startedAt);
        gain.gain.exponentialRampToValueAtTime(BEEP_GAIN, startedAt + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + BEEP_DURATION_S);

        oscillator.connect(gain);
        gain.connect(context.destination);

        oscillator.start(startedAt);
        oscillator.stop(startedAt + BEEP_DURATION_S + 0.02);
      } catch (error) {
        // Un bip raté n'interrompt pas le scan.
      }
    },
    releaseBeep() {
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
    },
    /**
     * Traduit l'erreur de getUserMedia en consigne actionnable : refuser l'autorisation et
     * ne pas avoir de caméra n'appellent pas le même geste.
     */
    describeError(error) {
      const name = error && error.name ? error.name : "";

      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        // Plusieurs causes derrière le même nom, et aucune n'est forcément un refus de
        // l'utilisateur : le navigateur bloque aussi de lui-même après quelques demandes
        // restées sans réponse (« embargo » : plus aucune bulle n'est proposée pendant
        // plusieurs jours), et Chrome refuse les permissions sensibles aux pages dont le
        // certificat HTTPS est invalide. D'où la consigne de réinitialiser plutôt que
        // d'autoriser : c'est le seul geste qui couvre les trois cas.
        return "L'appareil photo n'est pas autorisé pour ce site. Ouvrez les informations du site (l'icône à gauche de l'adresse), réinitialisez l'autorisation de l'appareil photo, puis rechargez la page.";
      }
      if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return "Aucun appareil photo n'a été trouvé sur cet appareil.";
      }
      if (name === "NotReadableError" || name === "TrackStartError") {
        return "L'appareil photo est déjà utilisé par une autre application.";
      }
      if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
        return "Aucun appareil photo ne correspond aux réglages demandés.";
      }
      if (name === "ViewportMissing") {
        return "Le viseur n'a pas pu être affiché. Quittez le mode photo et rouvrez-le.";
      }

      return "Impossible d'ouvrir l'appareil photo.";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.camera-scanner.is-inset {
  padding: 0 25px 10px;
}

.camera-viewport {
  position: relative;
  width: 100%;
  // Le viseur ne prend qu'un tiers de la hauteur : au-dessus de lui, la barre de scan étant
  // ancrée en bas de l'écran, c'est la liste des références qui reste visible — et c'est
  // elle qui dit au préparateur que le scan a produit son effet. Le plafond en pixels évite
  // qu'il ne s'étire sur une tablette ou sur un poste de bureau.
  height: 32vh;
  max-height: 260px;
  overflow: hidden;
  background: #000000;
  border-radius: 4px;

  video {
    width: 100%;
    height: 100%;
    // `cover` : l'image remplit le cadre sans bande noire, quitte à rogner les bords — le
    // code est présenté au centre.
    object-fit: cover;
  }
}

// Réticule : il ne masque rien, il indique seulement où présenter l'étiquette.
.camera-reticle {
  position: absolute;
  top: 20%;
  left: 10%;
  width: 80%;
  height: 60%;
  border: 2px solid rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
  transition: border-color 0.15s ease-in-out;
}

// Lecture réussie : le réticule passe au vert le temps d'être vu.
.is-hit .camera-reticle {
  border-color: $primary;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25), 0 0 12px 2px $primary;
}

.camera-starting {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  color: #ffffff;
  text-align: center;
}

// Bandeau de confirmation. Il garde une hauteur constante, consigne ou lecture : le viseur
// ne doit pas sauter d'un pixel à chaque code lu.
.camera-status {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  margin: 8px 0 0;
  padding: 0 10px;
  border-radius: 4px;
  background-color: $bg-2;
  color: $text-50p;
  font-size: 13px;
  text-align: center;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;

  i {
    margin-right: 6px;
  }

  &.is-hit {
    background-color: $success;
    color: $primary-darker;
    @extend .clanprobold;
  }
}

.camera-count {
  margin-left: 8px;
  color: $text-50p;
  font-size: 11px;
  @extend .clanprobook;
}

// Le CSS mobile global donne aux boutons des barres d'actions 20 px de padding vertical et
// toute la largeur : ici la place est comptée, le bouton se contente de sa hauteur utile.
.camera-scanner .camera-close.btn {
  width: 100%;
  margin-top: 8px;
  padding: 10px 0;
  font-size: 12px;
}

.camera-error {
  padding: 15px 0;
  text-align: center;

  i {
    color: $red;
  }

  p {
    margin: 0 0 12px;
  }
}

// Information de diagnostic : lisible si on la cherche, invisible si on ne la cherche pas.
.camera-error-detail {
  margin: 12px 0 0;
  font-size: 0.75rem;
  color: $text-50p;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/CameraScanner.vue